import React, { useEffect, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  TextField,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import { theme } from '../../../../../theme';
import { HiringFlowDropdownType } from './CreateJob';
import { statusColorMap } from './ReadJob';

interface HiringFlowProps {
  selectedStages: string[];
  setSelectedStages: (value: string[]) => void;
  stages: HiringFlowDropdownType[];
  setHiringFlowEditMode: (value: boolean) => void;
  hiringFlowEditMode: boolean;
  hirigFlowData: any;
  showEditHiringFlow: boolean;
}

const HiringFlowDropdown: React.FC<HiringFlowProps> = ({
  selectedStages,
  setSelectedStages,
  stages,
  setHiringFlowEditMode,
  hiringFlowEditMode,
  hirigFlowData,
  showEditHiringFlow,
}) => {
  const SELECT_ALL_KEY = '__SELECT_ALL__';

  const fixedStageNames = useMemo(
    () => stages.filter((s) => s.StepName === 'Rejected' || s.StepName === 'On Hold').map((s) => s.StepName),
    [stages]
  );

  const existingFixedStages = useMemo(
    () =>
      hirigFlowData?.map((stage: any) => stage.CandidateStatusKey)
        .filter((key: string) => fixedStageNames.includes(key)) || [],
    [hirigFlowData, fixedStageNames]
  );

  const fixedStages = useMemo(
    () => (hiringFlowEditMode ? fixedStageNames : existingFixedStages),
    [hiringFlowEditMode, fixedStageNames, existingFixedStages]
  );

  useEffect(() => {
    if (
      hirigFlowData &&
      hirigFlowData.length > 0 &&
      selectedStages.length === 0
    ) {
      const initialStages = hirigFlowData.map((stage: any) => stage.CandidateStatusKey);
      const uniqueStages = Array.from(new Set(initialStages));
      const allStages = [
        ...uniqueStages.filter(stage => !fixedStages.includes(stage)),
        ...fixedStages,
      ];
      setSelectedStages(allStages);
    }
  }, [hirigFlowData, fixedStages, selectedStages, setSelectedStages]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !hiringFlowEditMode) return;

    const items = Array.from(selectedStages.filter(s => !fixedStages.includes(s)));
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setSelectedStages([...items, ...fixedStages]);
  };

  const handleRemove = (stage: string) => {
    if (!hiringFlowEditMode || fixedStages.includes(stage)) return;
    setSelectedStages(selectedStages.filter((s) => s !== stage));
  };

  const allStageNames = stages.map((s) => s.StepName);
  const dropdownOptions = [SELECT_ALL_KEY, ...allStageNames];
  const userSelectedStages = selectedStages.filter((s) => !fixedStages.includes(s));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography fontWeight={600}>
          Hiring Flow <span style={{ color: theme.palette.error.main }}>*</span>
        </Typography>

        {showEditHiringFlow && (
          hiringFlowEditMode ? (
            <Button
              variant="contained"
              disabled={userSelectedStages.length === 0}
              onClick={() => setHiringFlowEditMode(false)}
            >
              Save Flow
            </Button>
          ) : (
            <Button variant="outlined" onClick={() => setHiringFlowEditMode(true)}>
              Edit Flow
            </Button>
          )
        )}
      </Box>

      <Autocomplete
        multiple
        size='small'
        disableCloseOnSelect
        options={dropdownOptions}
        value={[
          ...userSelectedStages,
          ...fixedStages,
        ]}
        onChange={(event, newValue, reason, details) => {
          if (!hiringFlowEditMode) return;

          if (details?.option && fixedStages.includes(details.option)) return;

          if (newValue.includes(SELECT_ALL_KEY)) {
            const selectable = allStageNames.filter((s) => !fixedStages.includes(s));
            if (userSelectedStages.length === selectable.length) {
              setSelectedStages([...fixedStages]); // Deselect all
            } else {
              setSelectedStages([...selectable, ...fixedStages]); // Select all
            }
          } else {
            const filtered = newValue.filter((v) => v !== SELECT_ALL_KEY && !fixedStages.includes(v));
            const withFixed = Array.from(new Set([...filtered, ...fixedStages]));
            setSelectedStages(withFixed);
          }
        }}
        disabled={!hiringFlowEditMode}
        renderTags={(value: string[], getTagProps) =>
          value
            .filter((option) => option !== SELECT_ALL_KEY)
            .map((option: string, index: number) => {
              const colorObj = statusColorMap[option] || {
                color: '#000',
                bgColor: '#fff',
                borderColor: '#ccc',
              };
              const isFixed = fixedStages.includes(option);
              return (
                <Box
                  key={option}
                  sx={{
                    backgroundColor: colorObj.bgColor,
                    borderRadius: '16px',
                    px: 1.5,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    m: 0.5,
                    opacity: isFixed ? 0.8 : 1,
                  }}
                >
                  <Typography variant="body2" sx={{ mr: 1, color: colorObj.color }}>
                    {option}
                  </Typography>
                  {hiringFlowEditMode && !isFixed && (
                    <CloseIcon
                      fontSize="small"
                      {...getTagProps({ index }).onDelete}
                      onClick={() => handleRemove(option)}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </Box>
              );
            })
        }
        renderOption={(props, option, { selected }) => {
          const isFixed = fixedStages.includes(option);
          const selectableStages = allStageNames.filter(s => !fixedStages.includes(s));
          const isAllSelected = selectableStages.every(stage => selectedStages.includes(stage));

          const isChecked = option === SELECT_ALL_KEY
            ? isAllSelected
            : selected || isFixed;

          return (
            <li {...props}>
              <Checkbox
                checked={isChecked}
                disabled={isFixed && option !== SELECT_ALL_KEY}
              />
              {option === SELECT_ALL_KEY ? 'Select All' : option}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select stages"
            inputProps={{
              ...params.inputProps,
              readOnly: !hiringFlowEditMode,
            }}
          />
        )}
        sx={{ width: '100%', mb: 2, height: '4vw' }}
      />

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="selected-stages" direction="horizontal">
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {userSelectedStages.map((stage: string, index: number) => {
                const colorObj = statusColorMap[stage] || {
                  color: '#000',
                  bgColor: '#fff',
                  borderColor: '#ccc',
                };

                return (
                  <Draggable
                    key={stage}
                    draggableId={stage}
                    index={index}
                    isDragDisabled={!hiringFlowEditMode}
                  >
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          backgroundColor: colorObj.bgColor,
                          color: colorObj.color,
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          maxWidth: '12vw',
                          height: '5vw',
                          px: 2,
                        }}
                      >
                        <Typography sx={{ mr: 1 }}>{stage}</Typography>
                        {hiringFlowEditMode && (
                          <IconButton size="small" onClick={() => handleRemove(stage)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Draggable>
                );
              })}

              {fixedStages.map((stage: string) => {
                const colorObj = statusColorMap[stage] || {
                  color: '#000',
                  bgColor: '#fff',
                  borderColor: '#ccc',
                };
                return (
                  <Box
                    key={stage}
                    sx={{
                      backgroundColor: colorObj.bgColor,
                      color: colorObj.color,
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      maxWidth: '12vw',
                      height: '5vw',
                      px: 2,
                      opacity: 0.8,
                    }}
                  >
                    <Typography sx={{ mr: 1 }}>{stage}</Typography>
                  </Box>
                );
              })}

              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default HiringFlowDropdown;
